import React, { useEffect, useState, useMemo } from 'react';
import axios from '../configApi/axiosConfig';
import { APIURL } from '../configApi/apiConfig';
import { useSnackbar } from 'notistack';
import Layout from '../components/Layout';
import * as XLSX from 'xlsx';
import {
  FiSearch,
  FiChevronUp,
  FiChevronDown,
  FiX,
  FiInbox,
  FiFileText,
  FiBarChart2,
  FiCheckCircle,
  FiRefreshCw,
  FiSend,
  FiAlertTriangle,
  FiCalendar,
  FiSmile,
  FiTool,
  FiCheckSquare,
  FiClock,
  FiTarget,
  FiUsers,
  FiAlertCircle,
  FiTrendingUp,
} from 'react-icons/fi';

/* ────────────── COLOR TOKENS (paleta fija de estado — nunca decorativa) ────────────── */
// good/warning/serious/critical: escala fija reservada para semáforos de desempeño.
const ESTADO_COLOR = {
  bueno: '#0ca30c',
  atencion: '#fab219',
  riesgo: '#ec835a',
  critico: '#d03b3b',
  sinDatos: '#898781',
};
// Par divergente (negativo↔positivo) para las barras de distribución tipo Likert,
// centradas en el neutro. Dos matices + gris neutro, nunca una escala arcoíris.
const DIVERGENTE = {
  negFuerte: '#e34948',
  negLeve: '#f0a19f',
  neutro: '#c3c2b7',
  posLeve: '#86b6ef',
  posFuerte: '#2a78d6',
};
const UMBRAL_MUESTRA_MINIMA = 5;

// Dobla la escala de 5 niveles del usuario (≥90 excelente / 80-89 bueno / 70-79
// atención / 60-69 riesgo / <60 crítico) sobre los 4 roles fijos de estado.
const nivelDesempeno = (pct, total) => {
  if (total < UMBRAL_MUESTRA_MINIMA) {
    return { label: total === 0 ? 'Sin datos' : 'Muestra insuficiente', color: ESTADO_COLOR.sinDatos, bg: '#89878114' };
  }
  if (pct >= 80) return { label: 'Buen nivel', color: ESTADO_COLOR.bueno, bg: '#0ca30c14' };
  if (pct >= 70) return { label: 'Atención', color: ESTADO_COLOR.atencion, bg: '#fab21920' };
  if (pct >= 60) return { label: 'Riesgo', color: ESTADO_COLOR.riesgo, bg: '#ec835a1c' };
  return { label: 'Crítico', color: ESTADO_COLOR.critico, bg: '#d03b3b14' };
};

/* ────────────── ICONS ────────────── */
const SearchIcon = () => <FiSearch className="h-4 w-4" />;
const ChevronUpIcon = () => <FiChevronUp className="h-3 w-3" />;
const ChevronDownIcon = () => <FiChevronDown className="h-3 w-3" />;
const SortBothIcon = () => (
  <span className="flex flex-col -space-y-1 text-gray-300">
    <FiChevronUp className="h-2.5 w-2.5" />
    <FiChevronDown className="h-2.5 w-2.5" />
  </span>
);
const ClearIcon = () => <FiX className="h-4 w-4" />;
const EmptyIcon = () => <FiInbox className="h-16 w-16 text-gray-200" />;
const ExcelIcon = () => <FiFileText className="h-4 w-4 text-emerald-600" />;
const TotalIcon = () => <FiBarChart2 className="h-6 w-6" />;

/* ────────────── CATÁLOGO REAL DE ESTADOS (EstadoLhiaDT) ────────────── */
// 4 = ENCUESTA RECEPTADA (verde: todo correcto, el cliente respondió la encuesta)
// 3 = ERROR ENVIO LHIA (rojo: falló el envío)
const ESTADOS_LHIA = {
  0: {
    label: 'Registro Ingresado', helper: 'Recién creado, aún sin enviar',
    icon: FiFileText,
    badge: 'bg-slate-100 text-slate-600 border-slate-200', dot: 'bg-slate-400',
    accent: { text: 'text-slate-700', bg: 'bg-slate-100', helper: 'text-slate-500', dot: 'bg-slate-400' },
    chip: 'bg-slate-600 text-white',
  },
  1: {
    label: 'Registro Actualizado', helper: 'En proceso técnico',
    icon: FiRefreshCw,
    badge: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500',
    accent: { text: 'text-amber-700', bg: 'bg-amber-50', helper: 'text-amber-600', dot: 'bg-amber-500' },
    chip: 'bg-amber-600 text-white',
  },
  2: {
    label: 'Encuesta Enviada LHIA', helper: 'Esperando respuesta del cliente',
    icon: FiSend,
    badge: 'bg-blue-50 text-blue-700 border-blue-200', dot: 'bg-blue-500',
    accent: { text: 'text-blue-700', bg: 'bg-blue-50', helper: 'text-blue-600', dot: 'bg-blue-500' },
    chip: 'bg-blue-600 text-white',
  },
  3: {
    label: 'Error Envío LHIA', helper: 'Falló el envío de la encuesta',
    icon: FiAlertTriangle,
    badge: 'bg-rose-50 text-rose-700 border-rose-200', dot: 'bg-rose-500',
    accent: { text: 'text-rose-700', bg: 'bg-rose-50', helper: 'text-rose-600', dot: 'bg-rose-500' },
    chip: 'bg-rose-600 text-white',
  },
  4: {
    label: 'Encuesta Receptada', helper: 'Respuesta completada correctamente',
    icon: FiCheckCircle,
    badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500',
    accent: { text: 'text-emerald-700', bg: 'bg-emerald-50', helper: 'text-emerald-600', dot: 'bg-emerald-500' },
    chip: 'bg-emerald-600 text-white',
  },
};
const ESTADO_OTRO = {
  label: 'Otro estado', helper: '',
  icon: FiFileText,
  badge: 'bg-slate-100 text-slate-600 border-slate-200', dot: 'bg-slate-400',
  accent: { text: 'text-slate-700', bg: 'bg-slate-100', helper: 'text-slate-500', dot: 'bg-slate-400' },
  chip: 'bg-slate-600 text-white',
};

/* ────────────── ESCALAS DE RESPUESTA POR PREGUNTA (A-D) ────────────── */
// E es comentario libre, no tiene escala fija: no entra en este KPI.
// severidad: -2..+2 (posición real en el eje negativo↔positivo, independiente del
// orden en que vengan las opciones). score100: normaliza cada opción a 0-100 para
// el Índice General de Satisfacción. metricaLabel: cómo se llama el "% positivo"
// de esta pregunta en su tarjeta (cada pregunta usa su propia métrica, no una genérica).
const PREGUNTAS_ESCALA = {
  A: {
    titulo: 'Satisfacción General', corta: 'CSAT', icon: FiSmile, metricaLabel: 'satisfecho', escala5: true,
    opciones: [
      { label: 'Muy insatisfecho', sentimiento: 'negativo', severidad: -2, score100: 0 },
      { label: 'Insatisfecho', sentimiento: 'negativo', severidad: -1, score100: 25 },
      { label: 'Regular', sentimiento: 'neutro', severidad: 0, score100: 50 },
      { label: 'Satisfecho', sentimiento: 'positivo', severidad: 1, score100: 75 },
      { label: 'Muy satisfecho', sentimiento: 'positivo', severidad: 2, score100: 100 },
    ],
  },
  B: {
    titulo: 'Atención del Técnico', corta: 'Técnico', icon: FiTool, metricaLabel: 'bien calificado', escala5: true,
    opciones: [
      { label: 'Muy mala', sentimiento: 'negativo', severidad: -2, score100: 0 },
      { label: 'Mala', sentimiento: 'negativo', severidad: -1, score100: 25 },
      { label: 'Regular', sentimiento: 'neutro', severidad: 0, score100: 50 },
      { label: 'Buena', sentimiento: 'positivo', severidad: 1, score100: 75 },
      { label: 'Excelente', sentimiento: 'positivo', severidad: 2, score100: 100 },
    ],
  },
  C: {
    titulo: 'Conformidad con el Cambio', corta: 'Conformidad', icon: FiCheckSquare, metricaLabel: 'conforme', escala5: false,
    opciones: [
      { label: 'Sí', sentimiento: 'positivo', severidad: 2, score100: 100 },
      { label: 'Parcialmente', sentimiento: 'neutro', severidad: 0, score100: 50 },
      { label: 'No', sentimiento: 'negativo', severidad: -2, score100: 0 },
    ],
  },
  D: {
    titulo: 'Tiempo de Atención', corta: 'Tiempo', icon: FiClock, metricaLabel: 'dentro de lo esperado', escala5: true,
    opciones: [
      { label: 'Muy rápido', sentimiento: 'positivo', severidad: 2, score100: 100 },
      { label: 'Rápido', sentimiento: 'positivo', severidad: 1, score100: 75 },
      { label: 'Normal', sentimiento: 'neutro', severidad: 0, score100: 50 },
      { label: 'Lento', sentimiento: 'negativo', severidad: -1, score100: 25 },
      { label: 'Muy lento', sentimiento: 'negativo', severidad: -2, score100: 0 },
    ],
  },
};

const colorSeveridad = (severidad) => {
  if (severidad <= -2) return DIVERGENTE.negFuerte;
  if (severidad < 0) return DIVERGENTE.negLeve;
  if (severidad === 0) return DIVERGENTE.neutro;
  if (severidad < 2) return DIVERGENTE.posLeve;
  return DIVERGENTE.posFuerte;
};

/* ────────────── BARRA DIVERGENTE (Likert, centrada en el neutro) ────────────── */
const DivergingBar = ({ opciones, distribucion, total }) => {
  const negativos = opciones.filter(o => o.sentimiento === 'negativo').sort((a, b) => a.severidad - b.severidad);
  const positivos = opciones.filter(o => o.sentimiento === 'positivo').sort((a, b) => a.severidad - b.severidad);
  const neutro = opciones.find(o => o.sentimiento === 'neutro');
  const negTotal = negativos.reduce((s, o) => s + (distribucion[o.label] || 0), 0);
  const posTotal = positivos.reduce((s, o) => s + (distribucion[o.label] || 0), 0);
  const neutroCount = neutro ? (distribucion[neutro.label] || 0) : 0;

  const segmento = (op, key) => {
    const count = distribucion[op.label] || 0;
    if (count === 0) return null;
    return <span key={key} title={`${op.label}: ${count}`} className="h-full" style={{ width: `${(count / (op.__mitad === 'neg' ? negTotal : posTotal)) * 100}%`, background: colorSeveridad(op.severidad) }} />;
  };

  return (
    <div className="flex items-center h-3 w-full" style={{ gap: '2px' }}>
      <div className="flex-1 flex justify-end h-full">
        <div className="flex h-full rounded-l-full overflow-hidden" style={{ width: total ? `${(negTotal / total) * 100}%` : 0, gap: '2px' }}>
          {negativos.map(op => segmento({ ...op, __mitad: 'neg' }, op.label))}
        </div>
      </div>
      <span className="h-full shrink-0 rounded-full" style={{ width: neutroCount > 0 ? `${Math.max((neutroCount / (total || 1)) * 100, 3)}%` : '2px', background: neutroCount > 0 ? DIVERGENTE.neutro : '#e1e0d9' }} title={neutro ? `${neutro.label}: ${neutroCount}` : undefined} />
      <div className="flex-1 flex justify-start h-full">
        <div className="flex h-full rounded-r-full overflow-hidden" style={{ width: total ? `${(posTotal / total) * 100}%` : 0, gap: '2px' }}>
          {positivos.map(op => segmento({ ...op, __mitad: 'pos' }, op.label))}
        </div>
      </div>
    </div>
  );
};

/* ────────────── QUESTION KPI CARD ────────────── */
const QuestionKpiCard = ({ letra, config, stats, onFiltrar }) => {
  const Icon = config.icon;
  const { total, distribucion, pctPositivo, promedio } = stats;
  const nivel = nivelDesempeno(pctPositivo, total);
  const opcionesOrden = [...config.opciones].sort((a, b) => b.severidad - a.severidad); // mejor→peor, como el mockup

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm flex flex-col gap-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="w-7 h-7 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
            <Icon className="h-4 w-4" />
          </span>
          <div>
            <span className="text-[10px] font-bold text-indigo-400 uppercase tracking-wider">Pregunta {letra}</span>
            <p className="text-xs font-semibold text-slate-700 leading-tight">{config.titulo}</p>
          </div>
        </div>
        <span className="shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ color: nivel.color, background: nivel.bg }}>
          {nivel.label}
        </span>
      </div>

      <div className="flex items-baseline gap-1.5">
        <span className="text-2xl font-bold" style={{ color: nivel.color }}>{total ? `${pctPositivo}%` : '—'}</span>
        <span className="text-[11px] text-slate-400 capitalize">{config.metricaLabel}</span>
      </div>

      {total > 0 ? (
        <>
          <DivergingBar opciones={config.opciones} distribucion={distribucion} total={total} />
          <ul className="flex flex-col gap-1">
            {opcionesOrden.map((op) => {
              const count = distribucion[op.label] || 0;
              return (
                <li key={op.label} className="flex items-center gap-2 text-[11px] text-slate-500">
                  <span className="w-24 shrink-0 truncate">{op.label}</span>
                  <span className="flex-1 h-1.5 rounded-full bg-slate-50 overflow-hidden">
                    <span className="block h-full rounded-full" style={{ width: `${(count / total) * 100}%`, background: colorSeveridad(op.severidad) }} />
                  </span>
                  <button
                    type="button"
                    onClick={() => count > 0 && onFiltrar(op.label)}
                    disabled={count === 0}
                    className={`w-5 text-right font-semibold tabular-nums ${count > 0 ? 'text-slate-700 hover:text-indigo-600 cursor-pointer' : 'text-slate-300 cursor-default'}`}
                    title={count > 0 ? `Ver en la tabla las respuestas "${op.label}"` : undefined}
                  >
                    {count}
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="flex items-center justify-between text-[10px] text-slate-400 pt-1 border-t border-slate-50">
            <span>{total} {total === 1 ? 'respuesta' : 'respuestas'}</span>
            {config.escala5 && <span className="font-medium text-slate-500">Promedio: {promedio.toFixed(1)}/5</span>}
          </div>
          {total < UMBRAL_MUESTRA_MINIMA && (
            <p className="text-[10px] text-slate-400 italic -mt-1">⚠ Muestra insuficiente para conclusiones sólidas.</p>
          )}
        </>
      ) : (
        <p className="text-[11px] text-slate-300 italic">Aún no hay respuestas registradas para esta pregunta.</p>
      )}
    </div>
  );
};

/* ────────────── EVOLUCIÓN MENSUAL (línea, magnitud=secuencial azul) ────────────── */
const EvolucionChart = ({ puntos }) => {
  const W = 100; // viewBox en % — escala con el contenedor
  const H = 42;
  const PAD_X = 3;
  const stepX = (W - PAD_X * 2) / Math.max(puntos.length - 1, 1);
  const y = (v) => H - 6 - (v / 100) * (H - 12); // dejar aire arriba/abajo
  const coords = puntos.map((p, i) => ({ ...p, x: PAD_X + i * stepX, y: y(p.csat) }));
  const linea = coords.map(c => `${c.x},${c.y}`).join(' ');

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-32" preserveAspectRatio="none">
        {[0, 50, 100].map((g) => (
          <line key={g} x1={PAD_X} x2={W - PAD_X} y1={y(g)} y2={y(g)} stroke="#e1e0d9" strokeWidth="0.3" />
        ))}
        <polyline points={linea} fill="none" stroke="#2a78d6" strokeWidth="0.6" strokeLinejoin="round" strokeLinecap="round" />
        {coords.map((c, i) => (
          <g key={c.clave}>
            <circle cx={c.x} cy={c.y} r={i === coords.length - 1 ? 1.4 : 1} fill="#2a78d6" stroke="#fff" strokeWidth="0.5" />
            <title>{`${c.label}: ${c.csat}% CSAT · ${c.total} ${c.total === 1 ? 'respuesta' : 'respuestas'}`}</title>
          </g>
        ))}
        {coords.length > 0 && (
          <text x={coords[coords.length - 1].x} y={coords[coords.length - 1].y - 2.5} fontSize="3.2" fill="#0b0b0b" fontWeight="600" textAnchor="end">
            {coords[coords.length - 1].csat}%
          </text>
        )}
      </svg>
      <div className="flex justify-between px-[3%] mt-1">
        {coords.map(c => (
          <span key={c.clave} className="text-[10px] text-slate-400 capitalize">{c.label}</span>
        ))}
      </div>
    </div>
  );
};

/* ────────────── HELPERS ────────────── */
// Prioriza el código numérico real (EstadoCodigo); si no viene (p.ej. caché vieja
// de Redis previa a este cambio), cae a texto por compatibilidad.
const codigoEstado = (row) => {
  if (row?.EstadoCodigo !== undefined && row?.EstadoCodigo !== null) {
    return Number(row.EstadoCodigo);
  }
  const lower = (row?.EstadoDescripcion || '').toLowerCase();
  if (lower.includes('receptada')) return 4;
  if (lower.includes('error')) return 3;
  if (lower.includes('enviada')) return 2;
  if (lower.includes('actualizado')) return 1;
  if (lower.includes('ingresado')) return 0;
  return -1;
};
const estadoInfo = (codigo) => ESTADOS_LHIA[codigo] || ESTADO_OTRO;

/* ────────────── STATE BADGE ────────────── */
const StateBadge = ({ row }) => {
  const info = estadoInfo(codigoEstado(row));
  return (
    <span className={`inline-flex items-center gap-1.5 py-1 px-2.5 rounded-full text-xs font-semibold border whitespace-nowrap ${info.badge}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${info.dot}`}></span>
      {row.EstadoDescripcion}
    </span>
  );
};

/* ────────────── SORT BUTTON ────────────── */
const SortableHeader = ({ label, colKey, sortConfig, onSort }) => {
  const active = sortConfig.key === colKey;
  return (
    <th
      onClick={() => onSort(colKey)}
      className={`px-3 py-2.5 text-left text-xs font-bold uppercase tracking-wider cursor-pointer select-none whitespace-nowrap transition-colors group
        ${active ? 'bg-indigo-600 text-white' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'}`}
    >
      <div className="flex items-center gap-1.5">
        {label}
        <span className={`transition-opacity ${active ? 'opacity-100 text-indigo-200' : 'opacity-0 group-hover:opacity-60'}`}>
          {active ? (sortConfig.direction === 'asc' ? <ChevronUpIcon /> : <ChevronDownIcon />) : <SortBothIcon />}
        </span>
      </div>
    </th>
  );
};

/* ────────────── KPI CARD ────────────── */
const KpiCard = ({ label, value, helper, icon, accent }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm flex items-center justify-between">
    <div>
      <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</span>
      <div className={`text-2xl font-bold mt-1 ${accent.text}`}>{value}</div>
      <div className={`text-xs font-medium mt-1 flex items-center gap-1 ${accent.helper}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${accent.dot}`}></span> {helper}
      </div>
    </div>
    <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${accent.bg} ${accent.text}`}>
      {icon}
    </div>
  </div>
);

const FILAS_POR_PAGINA_OPCIONES = [15, 25, 50];

const hoyISO = () => {
  const d = new Date();
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d - tz).toISOString().slice(0, 10);
};

/* ────────────── MAIN COMPONENT ────────────── */
const ReporteEncuestas = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [fechaDesde, setFechaDesde] = useState(hoyISO());
  const [fechaHasta, setFechaHasta] = useState(hoyISO());
  const [sortConfig, setSortConfig] = useState({ key: 'Fecha', direction: 'desc' });
  const [filtroRapido, setFiltroRapido] = useState('todos');
  const [currentPage, setCurrentPage] = useState(1);
  const [filasPorPagina, setFilasPorPagina] = useState(15);
  const { enqueueSnackbar } = useSnackbar();

  const fetchReportData = async (desde, hasta) => {
    try {
      setLoading(true);
      const url = APIURL.getReporteEncuestas({ fechaDesde: desde, fechaHasta: hasta });
      const res = await axios.get(url);
      if (res.data?.success) {
        setData(res.data.data || []);
      } else {
        enqueueSnackbar('No se pudieron obtener los datos', { variant: 'error' });
      }
    } catch {
      enqueueSnackbar('Ocurrió un error al cargar el reporte', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Fechas "Desde" y "Hasta" son obligatorias y Desde no puede ser mayor a Hasta.
  const consultar = (desde = fechaDesde, hasta = fechaHasta) => {
    if (!desde || !hasta) {
      enqueueSnackbar('Debes seleccionar ambas fechas (Desde y Hasta)', { variant: 'warning' });
      return;
    }
    if (desde > hasta) {
      enqueueSnackbar('La fecha "Desde" no puede ser mayor a la fecha "Hasta"', { variant: 'warning' });
      return;
    }
    fetchReportData(desde, hasta);
  };

  // Carga el día actual al entrar a la página
  useEffect(() => {
    consultar(fechaDesde, fechaHasta);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tablaRef = React.useRef(null);
  const filtrarPorRespuesta = (label) => {
    setFiltroRapido('todos');
    setSearchTerm(label);
    tablaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  // Conteos por estado real — siempre sobre el dataset completo (sin filtro rápido aplicado)
  const conteos = useMemo(() => {
    const base = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, otro: 0 };
    data.forEach((r) => {
      const c = codigoEstado(r);
      base[c in base ? c : 'otro'] += 1;
    });
    return base;
  }, [data]);

  // Desempeño por pregunta (A-D): distribución + % positivo + score normalizado, sobre el dataset completo.
  const statsPorPregunta = useMemo(() => {
    const resultado = {};
    Object.keys(PREGUNTAS_ESCALA).forEach((letra) => {
      const config = PREGUNTAS_ESCALA[letra];
      const distribucion = {};
      let total = 0;
      let positivos = 0;
      let sumaScore100 = 0;
      data.forEach((row) => {
        const respuesta = (row[letra] || '').toString().trim();
        if (!respuesta) return;
        const opcion = config.opciones.find(o => o.label.toLowerCase() === respuesta.toLowerCase());
        if (!opcion) return;
        distribucion[opcion.label] = (distribucion[opcion.label] || 0) + 1;
        total += 1;
        sumaScore100 += opcion.score100;
        if (opcion.sentimiento === 'positivo') positivos += 1;
      });
      const score100Prom = total ? sumaScore100 / total : 0;
      resultado[letra] = {
        total,
        distribucion,
        pctPositivo: total ? Math.round((positivos / total) * 100) : 0,
        score100: score100Prom,
        promedio: score100Prom / 25 + 1, // 0-100 → 1-5
      };
    });
    return resultado;
  }, [data]);

  // Tamaño de muestra real: encuestas efectivamente respondidas por el cliente.
  const muestraTotal = conteos[4] || 0;

  // CSAT = % positivo de la pregunta de satisfacción general (A) — la definición estándar.
  const csat = statsPorPregunta.A?.pctPositivo || 0;

  // Índice General de Satisfacción (0-100): promedio simple de las preguntas con datos,
  // cada una ya normalizada a 0-100 vía score100.
  const preguntasConDatos = Object.values(statsPorPregunta).filter(s => s.total > 0);
  const indiceGeneral = preguntasConDatos.length
    ? Math.round(preguntasConDatos.reduce((s, p) => s + p.score100, 0) / preguntasConDatos.length)
    : null;

  // Alertas: respuestas negativas (y el "Parcialmente" de conformidad) que ameritan
  // seguimiento, más un reconocimiento a la pregunta con mejor desempeño.
  const alertas = useMemo(() => {
    const items = [];
    Object.entries(PREGUNTAS_ESCALA).forEach(([letra, config]) => {
      const stats = statsPorPregunta[letra];
      config.opciones
        .filter(op => op.sentimiento === 'negativo' || (letra === 'C' && op.sentimiento === 'neutro'))
        .forEach((op) => {
          const count = stats.distribucion[op.label] || 0;
          if (count === 0) return;
          items.push({
            key: `${letra}-${op.label}`,
            nivel: op.severidad <= -2 ? 'critico' : op.severidad === -1 ? 'riesgo' : 'atencion',
            texto: `${count} ${count === 1 ? 'cliente respondió' : 'clientes respondieron'} "${op.label}" en ${config.titulo}`,
            label: op.label,
          });
        });
    });
    items.sort((a, b) => ({ critico: 0, riesgo: 1, atencion: 2 }[a.nivel] - { critico: 0, riesgo: 1, atencion: 2 }[b.nivel]));

    const mejor = preguntasConDatos.length
      ? Object.entries(statsPorPregunta).filter(([, s]) => s.total > 0).sort((a, b) => b[1].pctPositivo - a[1].pctPositivo)[0]
      : null;
    if (mejor && mejor[1].pctPositivo >= 80) {
      items.push({
        key: `destacado-${mejor[0]}`,
        nivel: 'bueno',
        texto: `${PREGUNTAS_ESCALA[mejor[0]].titulo} con ${mejor[1].pctPositivo}% de valoración positiva`,
        label: null,
      });
    }
    return items;
  }, [statsPorPregunta, preguntasConDatos]);

  // Evolución mensual del CSAT (pregunta A), para detectar tendencia. Solo tiene sentido
  // si el rango consultado cubre más de un mes.
  const evolucionMensual = useMemo(() => {
    const meses = {};
    data.forEach((row) => {
      const respuesta = (row.A || '').toString().trim();
      if (!respuesta || !row.Fecha) return;
      const opcion = PREGUNTAS_ESCALA.A.opciones.find(o => o.label.toLowerCase() === respuesta.toLowerCase());
      if (!opcion) return;
      const clave = row.Fecha.toString().slice(0, 7); // YYYY-MM
      if (!meses[clave]) meses[clave] = { total: 0, positivos: 0 };
      meses[clave].total += 1;
      if (opcion.sentimiento === 'positivo') meses[clave].positivos += 1;
    });
    return Object.keys(meses).sort().map((clave) => {
      const [anio, mes] = clave.split('-');
      const nombreMes = new Date(Number(anio), Number(mes) - 1, 1).toLocaleDateString('es-EC', { month: 'short' });
      return { clave, label: nombreMes, total: meses[clave].total, csat: Math.round((meses[clave].positivos / meses[clave].total) * 100) };
    });
  }, [data]);

  const processedData = useMemo(() => {
    let rows = [...data];

    if (filtroRapido !== 'todos') {
      rows = rows.filter(r => codigoEstado(r) === Number(filtroRapido));
    }

    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      rows = rows.filter(r =>
        Object.values(r).some(v => v != null && v.toString().toLowerCase().includes(q))
      );
    }

    if (sortConfig.key) {
      rows.sort((a, b) => {
        const av = a[sortConfig.key];
        const bv = b[sortConfig.key];
        if (av == null) return 1;
        if (bv == null) return -1;
        const cmp = av < bv ? -1 : av > bv ? 1 : 0;
        return sortConfig.direction === 'asc' ? cmp : -cmp;
      });
    }

    return rows;
  }, [data, searchTerm, sortConfig, filtroRapido]);

  // Vuelve a la página 1 cada vez que cambian los filtros que afectan el resultado
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filtroRapido, filasPorPagina, data]);

  const totalPages = Math.max(1, Math.ceil(processedData.length / filasPorPagina));
  const startIndex = (currentPage - 1) * filasPorPagina;
  const endIndex = startIndex + filasPorPagina;
  const paginaActual = processedData.slice(startIndex, endIndex);

  const headers = [
    { key: 'BodegaNombre', label: 'Bodega' },
    { key: 'ClienteRuc', label: 'RUC' },
    { key: 'ClienteNombre', label: 'Cliente' },
    { key: 'Ingreso', label: 'Ingreso' },
    { key: 'Fecha', label: 'Fecha' },
    { key: 'EstadoDescripcion', label: 'Estado' },
    { key: 'A', label: 'A' },
    { key: 'B', label: 'B' },
    { key: 'C', label: 'C' },
    { key: 'D', label: 'D' },
    { key: 'E', label: 'E' },
  ];

  const fmtDate = (v) => {
    if (!v) return '—';
    try {
      return new Date(v).toLocaleDateString('es-EC', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return v; }
  };

  const exportarExcel = () => {
    if (processedData.length === 0) {
      enqueueSnackbar('No hay datos para exportar', { variant: 'warning' });
      return;
    }
    const filas = processedData.map((r) => ({
      Bodega: r.BodegaNombre || '',
      RUC: r.ClienteRuc || '',
      Cliente: r.ClienteNombre || '',
      Ingreso: r.Ingreso || '',
      Fecha: fmtDate(r.Fecha),
      Estado: r.EstadoDescripcion || '',
      'A - ¿Qué tan satisfecho estás con nuestra atención?': r.A || '',
      'B - ¿Cómo fue la atención del técnico?': r.B || '',
      'C - ¿Quedaste conforme con el cambio de tu producto?': r.C || '',
      'D - ¿Cómo te pareció el tiempo de atención?': r.D || '',
      'E - ¿Quieres dejarnos algún comentario?': r.E || '',
    }));
    const ws = XLSX.utils.json_to_sheet(filas);
    ws['!cols'] = [
      { wch: 28 }, { wch: 15 }, { wch: 32 }, { wch: 16 }, { wch: 14 }, { wch: 20 },
      { wch: 38 }, { wch: 30 }, { wch: 38 }, { wch: 32 }, { wch: 34 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Encuestas');
    XLSX.writeFile(wb, 'Reporte_Encuestas_LHIA.xlsx');
  };

  const chips = [
    { key: 'todos', label: `Todos (${data.length})`, activeClasses: 'bg-indigo-600 text-white' },
    ...Object.entries(ESTADOS_LHIA).map(([codigo, info]) => ({
      key: codigo,
      label: `${info.label} (${conteos[codigo]})`,
      activeClasses: info.chip,
    })),
  ];

  /* ────────────── RENDER ────────────── */
  return (
    <>
      <Layout />

      <div
        className="flex flex-col"
        style={{
          marginLeft: '64px',
          marginTop: '64px',
          minHeight: 'calc(100vh - 64px)',
          background: 'linear-gradient(135deg, #f0f4ff 0%, #faf5ff 50%, #f0fdf4 100%)',
          padding: '16px 20px 24px',
        }}
      >

        {/* ── PAGE HEADER ── */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
          <div className="flex items-start gap-3">
            <div className="w-1.5 h-11 bg-indigo-600 rounded-full mt-0.5"></div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-lg font-extrabold text-slate-800 tracking-tight">Reporte de Encuestas</h1>
                {!loading && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-600 text-white shadow-sm">
                    Total: {data.length}
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-0.5">Gestión y visualización de encuestas LHIA para control de calidad y soporte técnico</p>
            </div>
          </div>

          <button
            onClick={exportarExcel}
            className="inline-flex items-center gap-2 px-3.5 py-2 text-xs font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 shadow-sm transition-all self-start md:self-auto"
          >
            <ExcelIcon />
            Exportar Excel
          </button>
        </div>

        {/* ── KPIs DE SATISFACCIÓN (resultado) ── */}
        {!loading && (
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-1 h-4 bg-indigo-500 rounded-full"></span>
              <h2 className="text-xs font-bold text-slate-700 uppercase tracking-wide">KPIs de Satisfacción</h2>
              <span className="text-[10px] text-slate-400">— resultado, no volumen de registros</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {(() => {
                const nivelCsat = nivelDesempeno(csat, muestraTotal);
                const nivelIndice = indiceGeneral !== null ? nivelDesempeno(indiceGeneral, muestraTotal) : nivelDesempeno(0, 0);
                const tasaRespuesta = data.length ? Math.round((muestraTotal / data.length) * 100) : 0;
                return (
                  <>
                    <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm flex flex-col items-center text-center gap-1">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1"><FiTarget className="h-3 w-3" /> CSAT</span>
                      <span className="text-3xl font-bold" style={{ color: nivelCsat.color }}>{muestraTotal ? `${csat}%` : '—'}</span>
                      <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ color: nivelCsat.color, background: nivelCsat.bg }}>{nivelCsat.label}</span>
                    </div>
                    <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm flex flex-col items-center text-center gap-1">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1"><FiBarChart2 className="h-3 w-3" /> Índice General</span>
                      <span className="text-3xl font-bold" style={{ color: nivelIndice.color }}>{indiceGeneral !== null ? `${indiceGeneral}/100` : '—'}</span>
                      <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ color: nivelIndice.color, background: nivelIndice.bg }}>{nivelIndice.label}</span>
                    </div>
                    <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm flex flex-col items-center text-center gap-1">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1"><FiUsers className="h-3 w-3" /> Respuestas</span>
                      <span className="text-3xl font-bold text-indigo-600">{muestraTotal} <span className="text-base text-slate-300 font-medium">/ {data.length}</span></span>
                      <span className="text-[11px] text-slate-400">Tasa de respuesta: {tasaRespuesta}%</span>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {/* ── DESEMPEÑO POR PREGUNTA ── */}
        {!loading && (
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-1 h-4 bg-indigo-400 rounded-full"></span>
              <h2 className="text-xs font-bold text-slate-600 uppercase tracking-wide">Desempeño por Pregunta</h2>
              <span className="text-[10px] text-slate-400">— distribución real de respuestas por criterio</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.entries(PREGUNTAS_ESCALA).map(([letra, config]) => (
                <QuestionKpiCard key={letra} letra={letra} config={config} stats={statsPorPregunta[letra]} onFiltrar={filtrarPorRespuesta} />
              ))}
            </div>
          </div>
        )}

        {/* ── ALERTAS ── */}
        {!loading && (
          <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm mb-4">
            <div className="flex items-center gap-2 mb-3">
              <FiAlertCircle className="h-4 w-4 text-slate-400" />
              <h2 className="text-xs font-bold text-slate-700 uppercase tracking-wide">Alertas</h2>
            </div>
            {muestraTotal === 0 ? (
              <p className="text-xs text-slate-400 italic">Aún no hay respuestas en el rango consultado para generar alertas.</p>
            ) : alertas.length === 0 ? (
              <p className="text-xs text-slate-500 flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full" style={{ background: ESTADO_COLOR.bueno }}></span> Sin alertas — todo en orden.</p>
            ) : (
              <ul className="flex flex-col gap-2">
                {alertas.map((a) => (
                  <li key={a.key}>
                    <button
                      type="button"
                      disabled={!a.label}
                      onClick={() => a.label && filtrarPorRespuesta(a.label)}
                      className={`w-full text-left flex items-center gap-2 text-xs px-2.5 py-1.5 rounded-lg transition ${a.label ? 'hover:bg-slate-50 cursor-pointer' : 'cursor-default'}`}
                    >
                      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: ESTADO_COLOR[a.nivel] }}></span>
                      <span className="text-slate-600">{a.texto}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* ── EVOLUCIÓN DE SATISFACCIÓN ── */}
        {!loading && (
          <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm mb-4">
            <div className="flex items-center gap-2 mb-3">
              <FiTrendingUp className="h-4 w-4 text-slate-400" />
              <h2 className="text-xs font-bold text-slate-700 uppercase tracking-wide">Evolución de Satisfacción (CSAT)</h2>
            </div>
            {evolucionMensual.length < 2 ? (
              <p className="text-xs text-slate-400 italic">Amplía el rango de fechas (más de un mes) para ver la tendencia mensual del CSAT.</p>
            ) : (
              <EvolucionChart puntos={evolucionMensual} />
            )}
          </div>
        )}

        {/* ── ESTADO OPERATIVO ── */}
        {!loading && (
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-1 h-4 bg-slate-300 rounded-full"></span>
              <h2 className="text-xs font-bold text-slate-500 uppercase tracking-wide">Estado Operativo</h2>
              <span className="text-[10px] text-slate-400">— registros, no resultado de la encuesta</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              <KpiCard
                label="Total Evaluadas"
                value={data.length}
                helper="100% de la muestra actual"
                icon={<TotalIcon />}
                accent={{ text: 'text-indigo-700', bg: 'bg-indigo-50', helper: 'text-indigo-600', dot: 'bg-indigo-500' }}
              />
              {Object.entries(ESTADOS_LHIA).map(([codigo, info]) => {
                const Icon = info.icon;
                return (
                  <KpiCard
                    key={codigo}
                    label={info.label}
                    value={conteos[codigo]}
                    helper={info.helper}
                    icon={<Icon className="h-6 w-6" />}
                    accent={info.accent}
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* ── TOOLBAR ── */}
        <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm space-y-3 mb-4">
          <div className="flex flex-wrap items-center gap-2">
            {/* Search */}
            <div className="relative flex-1 min-w-[180px] max-w-sm">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400">
                <SearchIcon />
              </span>
              <input
                type="text"
                placeholder="Buscar por cliente, bodega, RUC, estado…"
                className="w-full pl-8 pr-8 py-2 rounded-lg border border-slate-200 bg-slate-50 shadow-sm text-xs text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent focus:bg-white transition-all"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition-colors"
                >
                  <ClearIcon />
                </button>
              )}
            </div>

            {/* Date range */}
            <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5">
              <FiCalendar className="h-3.5 w-3.5 text-slate-400 shrink-0" />
              <span className="text-xs text-slate-400 font-medium">Desde</span>
              <input
                type="date"
                required
                max={fechaHasta || undefined}
                className="text-xs text-slate-700 bg-transparent border-none outline-none cursor-pointer w-28"
                value={fechaDesde}
                onChange={(e) => setFechaDesde(e.target.value)}
              />
              <span className="text-slate-300 text-xs">—</span>
              <span className="text-xs text-slate-400 font-medium">Hasta</span>
              <input
                type="date"
                required
                min={fechaDesde || undefined}
                className="text-xs text-slate-700 bg-transparent border-none outline-none cursor-pointer w-28"
                value={fechaHasta}
                onChange={(e) => setFechaHasta(e.target.value)}
              />
              <button
                onClick={() => {
                  const hoy = hoyISO();
                  setFechaDesde(hoy);
                  setFechaHasta(hoy);
                  consultar(hoy, hoy);
                }}
                className="ml-1 text-slate-400 hover:text-indigo-600 transition-colors"
                title="Volver a hoy"
              >
                <ClearIcon />
              </button>
            </div>

            <button
              onClick={() => consultar()}
              disabled={loading || !fechaDesde || !fechaHasta}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 disabled:cursor-not-allowed text-white text-xs font-semibold shadow-sm transition-all"
            >
              <FiRefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              Consultar
            </button>
          </div>

          {/* Quick filter / filtro por estado */}
          <div className="flex items-center gap-2 pt-2 border-t border-slate-100 flex-wrap text-xs">
            <span className="text-slate-400 font-medium mr-1">Filtrar por estado:</span>
            {chips.map((chip) => (
              <button
                key={chip.key}
                onClick={() => setFiltroRapido(chip.key)}
                className={`px-3 py-1 rounded-full font-medium transition ${
                  filtroRapido === chip.key ? chip.activeClasses + ' shadow-xs' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                }`}
              >
                {chip.label}
              </button>
            ))}
            <div className="ml-auto text-xs text-slate-400 flex items-center gap-1">
              <span>Orden actual:</span>
              <span className="font-medium text-slate-700 bg-slate-100 px-2 py-0.5 rounded">
                {headers.find(h => h.key === sortConfig.key)?.label} ({sortConfig.direction === 'asc' ? '↑ asc' : '↓ desc'})
              </span>
            </div>
          </div>
        </div>

        {/* ── TABLE CARD ── */}
        <div ref={tablaRef} className="bg-white rounded-2xl shadow-[0_4px_24px_rgba(0,0,0,0.06)] border border-slate-100 overflow-hidden flex flex-col flex-1">

          {loading ? (
            <div className="flex flex-col items-center justify-center flex-1 py-32 gap-4">
              <div className="relative">
                <div className="h-12 w-12 rounded-full border-4 border-indigo-100 border-t-indigo-500 animate-spin"></div>
                <div className="h-8 w-8 rounded-full border-4 border-purple-100 border-t-purple-400 animate-spin absolute top-2 left-2" style={{ animationDirection: 'reverse', animationDuration: '0.6s' }}></div>
              </div>
              <p className="text-sm text-slate-400 font-medium">Consultando base de datos…</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      {headers.map((h) => (
                        <SortableHeader key={h.key} label={h.label} colKey={h.key} sortConfig={sortConfig} onSort={handleSort} />
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paginaActual.length === 0 ? (
                      <tr>
                        <td colSpan={headers.length} className="py-24 text-center">
                          <div className="flex flex-col items-center gap-3 text-slate-400">
                            <EmptyIcon />
                            <p className="text-base font-semibold text-slate-500">Sin resultados</p>
                            <p className="text-sm">No se encontraron encuestas{searchTerm ? ` para "${searchTerm}"` : ''}.</p>
                            {(searchTerm || filtroRapido !== 'todos') && (
                              <button
                                onClick={() => { setSearchTerm(''); setFiltroRapido('todos'); }}
                                className="mt-1 text-indigo-500 hover:text-indigo-700 text-sm font-medium underline underline-offset-2 transition-colors"
                              >
                                Limpiar filtros
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      paginaActual.map((row, i) => (
                        <tr
                          key={startIndex + i}
                          className="border-b border-slate-50 hover:bg-indigo-50/30 transition-colors duration-150 group"
                        >
                          {/* Bodega */}
                          <td className="px-3 py-2 text-xs whitespace-nowrap">
                            <span className="font-semibold text-slate-700">{row.BodegaNombre || '—'}</span>
                          </td>
                          {/* RUC */}
                          <td className="px-3 py-2 text-xs whitespace-nowrap">
                            <span className="font-mono text-slate-500 tracking-tight text-xs bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100">
                              {row.ClienteRuc || '—'}
                            </span>
                          </td>
                          {/* Cliente */}
                          <td className="px-3 py-2 text-xs max-w-[220px]">
                            <span className="font-semibold text-slate-800 line-clamp-1" title={row.ClienteNombre || ''}>{row.ClienteNombre || '—'}</span>
                          </td>
                          {/* Ingreso */}
                          <td className="px-3 py-2 text-xs whitespace-nowrap">
                            <span className="font-mono text-xs text-indigo-600 bg-indigo-50 border border-indigo-100 px-1.5 py-0.5 rounded font-semibold">
                              {row.Ingreso || '—'}
                            </span>
                          </td>
                          {/* Fecha */}
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-slate-500 font-medium">
                            {fmtDate(row.Fecha)}
                          </td>
                          {/* Estado */}
                          <td className="px-3 py-2 whitespace-nowrap">
                            <StateBadge row={row} />
                          </td>
                          {/* A–E responses */}
                          {['A', 'B', 'C', 'D', 'E'].map((col) => (
                            <td key={col} className="px-3 py-2 text-xs text-slate-600 max-w-[160px]">
                              <span className="line-clamp-1">{row[col] || <span className="text-slate-300 italic">—</span>}</span>
                            </td>
                          ))}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* ── PAGINATION FOOTER ── */}
              <div className="px-5 py-3.5 bg-slate-50 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
                <div className="flex items-center gap-4 text-slate-600">
                  <p>
                    Mostrando <span className="font-bold text-slate-900">{processedData.length === 0 ? 0 : startIndex + 1} - {Math.min(endIndex, processedData.length)}</span> de <span className="font-bold text-slate-900">{processedData.length}</span> encuestas
                  </p>
                  <div className="h-4 w-px bg-slate-300 hidden sm:block"></div>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500 hidden sm:inline">Filas por página:</span>
                    <select
                      className="bg-white border border-slate-300 text-slate-700 text-xs rounded-lg py-1 px-2.5 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                      value={filasPorPagina}
                      onChange={(e) => setFilasPorPagina(Number(e.target.value))}
                    >
                      {FILAS_POR_PAGINA_OPCIONES.map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <nav aria-label="Paginación de encuestas" className="flex items-center gap-1">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-2.5 py-1.5 rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-100 disabled:text-slate-300 disabled:cursor-not-allowed disabled:hover:bg-white text-xs font-medium transition"
                  >
                    Anterior
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                    .reduce((acc, p, idx, arr) => {
                      if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((p, idx) => p === '...' ? (
                      <span key={`ellipsis-${idx}`} className="px-2 py-1.5 text-slate-400">...</span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setCurrentPage(p)}
                        className={`w-8 h-8 rounded-lg font-semibold flex items-center justify-center text-xs transition ${
                          p === currentPage
                            ? 'bg-indigo-600 text-white shadow-xs'
                            : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-100 font-medium'
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-2.5 py-1.5 rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-100 disabled:text-slate-300 disabled:cursor-not-allowed disabled:hover:bg-white text-xs font-medium transition"
                  >
                    Siguiente
                  </button>
                </nav>
              </div>
            </>
          )}
        </div>

        {/* ── EVALUATION LEGEND ── */}
        <aside className="bg-white p-4 rounded-xl border border-slate-200 text-xs text-slate-500 flex flex-wrap items-center gap-x-4 gap-y-2 mt-4">
          <span className="font-semibold text-slate-700 uppercase tracking-wide text-[10px]">Guía de Criterios (A - E):</span>
          <span className="inline-flex items-center gap-1"><strong>A:</strong> ¿Qué tan satisfecho estás con nuestra atención?</span>
          <span className="text-slate-300">•</span>
          <span className="inline-flex items-center gap-1"><strong>B:</strong> ¿Cómo fue la atención del técnico?</span>
          <span className="text-slate-300">•</span>
          <span className="inline-flex items-center gap-1"><strong>C:</strong> ¿Quedaste conforme con el cambio de tu producto?</span>
          <span className="text-slate-300">•</span>
          <span className="inline-flex items-center gap-1"><strong>D:</strong> ¿Cómo te pareció el tiempo de atención?</span>
          <span className="text-slate-300">•</span>
          <span className="inline-flex items-center gap-1"><strong>E:</strong> ¿Quieres dejarnos algún comentario?</span>
        </aside>
      </div>
    </>
  );
};

export default ReporteEncuestas;
